/**
 * React Query hooks for yield events.
 * React Query hooks for the corresponding entity.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";
import { pollInsightsRefresh } from "./useInsightsQueries";
import {
  CROP_UNITS,
  CropYieldDetails,
  GRASS_UNITS,
  GrassYieldDetails,
  MEAT_UNITS,
  MeatYieldDetails,
  MILK_UNITS,
  MilkYieldDetails,
  REVENUE_CURRENCY,
  SOLD_OR_KEPT,
  YieldEvent,
} from "./yieldEventTypes";

// Re-export types for convenience
export {
  CROP_UNITS,
  GRASS_UNITS,
  MEAT_UNITS,
  MILK_UNITS,
  REVENUE_CURRENCY,
  SOLD_OR_KEPT,
};
export type {
  CropYieldDetails,
  GrassYieldDetails,
  MeatYieldDetails,
  MilkYieldDetails,
  YieldEvent,
};
// Export the API for direct use if needed
export { yieldEventApi };

class YieldAPI extends API<YieldEvent> {
  async getAllYieldEventsForFarm(
    farmGuid: string,
    yieldEventTimeAfter?: string
  ): Promise<YieldEvent[]> {
    const yieldEventTimeAfterParam = yieldEventTimeAfter
      ? `&yieldEventTimeAfter=${yieldEventTimeAfter}`
      : "";
    const res = await fetch(
      `${this.baseUrl}${this.entity}?farmGuid=${farmGuid}${yieldEventTimeAfterParam}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    return (await res.json()) as YieldEvent[];
  }
}

const yieldEventApi = new YieldAPI("yield");

// Event data changes infrequently after creation - use 5 minute stale time
// to reduce unnecessary refetches while keeping data reasonably fresh
const EVENT_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all yield events for a farm
 */
export function useYieldEvents(
  farmGuid: string | undefined,
  yieldEventTimeAfter?: string
) {
  return useQuery({
    queryKey: [...queryKeys.events.yield.all, farmGuid, yieldEventTimeAfter],
    queryFn: () =>
      yieldEventApi.getAllYieldEventsForFarm(
        farmGuid ?? "",
        yieldEventTimeAfter
      ),
    enabled: !!farmGuid,
    staleTime: EVENT_STALE_TIME,
  });
}

/**
 * Fetch a single yield event by guid
 */
export function useYieldEvent(guid: string | undefined) {
  return useQuery({
    queryKey: ["yieldEvent", guid],
    queryFn: () => yieldEventApi.getOne(guid ?? ""),
    enabled: !!guid,
    staleTime: EVENT_STALE_TIME,
  });
}

/**
 * Create a new yield event
 */
export function useCreateYieldEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      yieldEvent,
    }: {
      yieldEvent: YieldEvent;
      farmGuid: string;
    }) => {
      const result = await yieldEventApi.createOne(yieldEvent);
      return { ...yieldEvent, ...result };
    },
    onSuccess: async (_, { farmGuid }) => {
      // Invalidate yield events for the farm
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.yield.all,
      });
      // Also invalidate feed stock since yield events can affect it
      await queryClient.invalidateQueries({
        queryKey: queryKeys.feedStock.byFarm(farmGuid),
      });
      pollInsightsRefresh(queryClient, ["yieldSummary_", "feedInStore_"]);
    },
  });
}

/**
 * Update an existing yield event
 */
export function useUpdateYieldEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      yieldEvent,
    }: {
      yieldEvent: YieldEvent;
      farmGuid: string;
    }) => {
      const result = await yieldEventApi.updateOne(yieldEvent);
      return { ...yieldEvent, ...result };
    },
    onSuccess: async (updatedEvent, { farmGuid }) => {
      // Update the specific event in cache
      queryClient.setQueryData(["yieldEvent", updatedEvent.guid], updatedEvent);
      // Invalidate the list
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.yield.all,
      });
      // Also invalidate feed stock
      await queryClient.invalidateQueries({
        queryKey: queryKeys.feedStock.byFarm(farmGuid),
      });
      pollInsightsRefresh(queryClient, ["yieldSummary_", "feedInStore_"]);
    },
  });
}

/**
 * Delete a yield event
 */
export function useDeleteYieldEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (guid: string) => {
      await yieldEventApi.deleteOne(guid);
      return guid;
    },
    onSuccess: async (guid) => {
      queryClient.removeQueries({ queryKey: ["yieldEvent", guid] });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.yield.all,
      });
      pollInsightsRefresh(queryClient, ["yieldSummary_", "feedInStore_"]);
    },
  });
}
